import type { TooltipProps } from "recharts";
import {
  chartTooltipContentStyle,
  chartTooltipLabelStyle,
} from "@/lib/chart-styles";
import { cn } from "@/lib/utils";

// Tooltip that folds "Xxx (Historia)" / "Xxx (Ennuste)" series pairs into one legend entry and
// hides the null half that the past/future split always produces for the hovered slot.
export function ForecastTooltip({
  active,
  payload,
  formatValue,
}: TooltipProps<number, string> & {
  /** Per-series display override, keyed by the row's `dataKey` — e.g. DemandChart's
   *  −100…+100 KPI scores as "N %". Falls back to one decimal when omitted. */
  formatValue?: (dataKey: string, value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const entries = payload.filter(
    (p) => p.value !== null && p.value !== undefined,
  );
  if (entries.length === 0) return null;

  // The axis is keyed on a numeric step index (see operational-loop-data.ts), so the display
  // time comes from the hovered row's own payload, not the axis `label`.
  const time = (entries[0]?.payload as { time?: string } | undefined)?.time;
  const isForecast = entries.some((e) => String(e.dataKey).endsWith("Future"));

  return (
    <div style={chartTooltipContentStyle} className="px-2.5 py-1.5 shadow-lg">
      <div
        style={chartTooltipLabelStyle}
        className="flex items-center justify-between gap-4 text-[11px] font-medium"
      >
        <span>Klo {time ?? "—"}</span>
        <span
          className={cn(
            "text-[9px] uppercase tracking-wide",
            isForecast ? "text-primary" : "text-muted-foreground",
          )}
        >
          {isForecast ? "Ennuste" : "Historia"}
        </span>
      </div>
      <div className="grid gap-1">
        {entries.map((entry) => (
          <div
            key={String(entry.dataKey)}
            className="flex items-center justify-between gap-4 text-[11px]"
          >
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {String(entry.name)
                .replace(" (Historia)", "")
                .replace(" (Ennuste)", "")}
            </span>
            <span className="font-mono font-medium text-foreground">
              {typeof entry.value === "number"
                ? (formatValue?.(String(entry.dataKey), entry.value) ??
                  entry.value.toFixed(1))
                : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
